import React, { useState, useEffect } from 'react';
import SidePanel from '../SidePanel/SidePanel';
import {PAYMENT_SIDE_PANEL} from './../../config/SidePanelTypes';
import { getExpiringPayments } from '../../service/ExpiringPaymentsService';
import { formatDollarAmount, calculateDisplayPayments } from '../../util/EfupUtility';
import { toDateTimeString } from '../../util/DateUtility'
import { useCompany } from '../CompanySelection/CompanyContext';
import './ExpiringPayments.css';
import Pagination from '../Pagination/Pagination';
import LoadingSpinner from '../LoadingSpinner/LoadingSpinner';

export default function ExpiringPayments() {
    const [expiringPayments, setExpiringPayments] = useState();
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [selectedPage, setSelectedPage] = useState(1);
    const [isLoading, setIsLoading] = useState(false);
    const [hasError, setHasError] = useState(false);
    const [paymentUUID, setPaymentUUID] = useState("");
    const [paymentType, setPaymentType] = useState("");
    const pageSize = 10;
    const { companyNumber } = useCompany();

    function dataHandler(data) {
        setExpiringPayments(data.payments);
        setHasError(false);
        setIsLoading(false);
    }

    function errorHandler(error) {
        if (error?.response?.status !== 200 && error?.response?.status !== 404) {
            setHasError(true);
            setExpiringPayments([]);
        } else {
            setHasError(false);
        }
        setIsLoading(false);
    }

    useEffect(() => {
        setIsLoading(true);
        getExpiringPayments(dataHandler, errorHandler, companyNumber, 'SP');
    }, [companyNumber])

    function onRowSelect(uuid, type) {
        setPaymentUUID(uuid);
        setPaymentType(type);
        setIsModalOpen(true);
    }

    let dataToDisplay = calculateDisplayPayments(pageSize, selectedPage, expiringPayments);
    let totalExpiringPayments = expiringPayments ? expiringPayments.length : 0;

    const paginationElement = <React.Fragment>
        <div className="efup-expiring-payments-footer">
            <div className="efup-expiring-payments-footer-display" data-testid="efup-expiring-payments-footer-display">Showing {dataToDisplay.length} of {totalExpiringPayments}</div>
            <Pagination
                numberOfItems={totalExpiringPayments}
                selectedPage={selectedPage}
                setSelectedPage={setSelectedPage}
                pageSize={pageSize}
                type="expiring-payments"
                hidePageSize
            />
        </div>
        <SidePanel 
            isModalOpen={isModalOpen} 
            setIsModalOpen={setIsModalOpen}
            sidePanelType={PAYMENT_SIDE_PANEL}
            paymentUUID={paymentUUID}
            paymentType={paymentType} />
    </React.Fragment>;

    const emptyTableElement = <div className="efup-expiring-payments-no-results" data-testid="efup-expiring-payments-no-results">There are currently no access links expiring within 48 hours.</div>;

    const errorElement = <div data-testid="expiring-payments-unavailable">
        <h3>Expiring Payments is unavailable</h3>
    </div>;

    const loadingElement = <LoadingSpinner blue testId='payment-trends-loading-spinner' />;

    const tableElement = <div>
        <table className="table table-sort table-arrows cb-table">
            <thead className="table-light thead expiring-payments-header">
                <tr>
                    <th className="expiring-payment-header-payee">Party Name</th>
                    <th className="expiring-payment-header-number">Payment Number</th>
                    <th className="expiring-payment-header-amount">Amount</th>
                    <th className="expiring-payment-header-created-date">Created Date</th>
                    <th className="expiring-payment-header-expiration-date">Expiration Date</th>
                </tr>
            </thead>
            <tbody>
                {dataToDisplay && dataToDisplay.map((item, index) => {
                    let displayCreatedDate = (item.createdDate) ? toDateTimeString(item.createdDate) : null;
                    let displayExpireDate = (item.expireDate) ? toDateTimeString(item.expireDate) : null;
                    return <tr className="expiring-payments-row" key={`expiring-payment-${item.disbursementUuid}`} onClick={() => onRowSelect(item.disbursementUuid, item.paymentType)}>
                        <td className="expiring-payment-row-payee" data-testid={`expiring-payment-row-payee-${index}`}>{item.payeeName}</td>
                        <td className="expiring-payment-row-number" data-testid={`expiring-payment-row-number-${index}`}>{item.paymentNumber}</td>
                        <td className="expiring-payment-row-amount" data-testid={`expiring-payment-row-amount-${index}`}>{formatDollarAmount(item.amount)}</td>
                        <td className="expiring-payment-row-created-date" data-testid={`expiring-payment-row-created-date-${index}`}>{displayCreatedDate}</td>
                        <td className="expiring-payment-row-expiration-date" data-testid={`expiring-payment-row-expiration-date-${index}`}>{displayExpireDate}</td>
                    </tr>
                })}
            </tbody>
        </table>
        {dataToDisplay.length === 0 ? emptyTableElement : paginationElement}
    </div>;

    return (
        <div className="payment-search-wrapper dashBox cb-table-responsive payment-table efup-expiring-payments cb-font-poppins" data-testid="expiring-payments">
            <h5 className="efup-expiring-payments-title" data-testid="expiring-payments-title">Expiring Single Party Access Links</h5>
            <div className="tool-tip efup-expiring-payments-tooltip cb-font-poppins">!
                <span data-testid="efup-expiring-payments-tooltip" className="tooltiptext">
                    The widget only displays single party payments where the payee receives an invitation link through email and indicates
                    the payee's portal access will expire in 48 hours or less.
                </span>
            </div>
            {hasError ? errorElement :
                isLoading ? loadingElement :
                    tableElement
            }
        </div>
    );
}